import { useEffect, useCallback, useRef } from 'react';
import { useHQVDatalayerTrackingStore, HQVFallbackMode } from '../../modules/store/hqvDatalayerTrackingStore';
import { callDataLayer } from '../../organisms/HotelGalleryTitle/utils/helper';
import { logger } from '../../utils';

const { log } = logger({})('useHQVRetryOrchestration');

interface UseHQVRetryOrchestrationProps {
  resolvedUrl?: string;
  sessionData?: unknown;
  headersData?: unknown;
  query?: unknown;
  propertyId?: string;
  brandCode?: string;
  datalayerParams?: unknown;
  datalayerProperties?: unknown;
  currentLocale?: string;
  onCloseModal?: () => void;
}

interface UseHQVRetryOrchestrationReturn {
  hqvFallbackMode: HQVFallbackMode;
  isRetrying: boolean;
}

/**
 * Custom hook to orchestrate HQV retry logic when both PropertyInfo and PhotoGallery calls fail.
 *
 * Flow:
 * 1. Monitor initial PropertyInfo and PhotoGallery call statuses
 * 2. If both fail, trigger retry for both calls
 * 3. Monitor retry results and amenities call status
 * 4. Determine fallback mode based on results:
 *    - 'normal': At least one of PropertyInfo/PhotoGallery succeeded (initial or retry)
 *    - 'amenities_only': Both PropertyInfo and PhotoGallery failed but amenities succeeded
 *    - 'closed': All three calls failed
 * 5. Call data layer with appropriate fallback values
 * 6. Trigger modal close if all calls failed
 */
export const useHQVRetryOrchestration = ({
  resolvedUrl,
  sessionData,
  headersData,
  query,
  propertyId,
  brandCode,
  datalayerParams,
  datalayerProperties,
  currentLocale,
  onCloseModal,
}: UseHQVRetryOrchestrationProps): UseHQVRetryOrchestrationReturn => {
  // Store selectors
  const propertyInfoCallStatus = useHQVDatalayerTrackingStore(state => state.propertyInfoCallStatus);
  const photogalleryCallStatus = useHQVDatalayerTrackingStore(state => state.photogalleryCallStatus);
  const amenitiesCallStatus = useHQVDatalayerTrackingStore(state => state.amenitiesCallStatus);
  const retryAttempted = useHQVDatalayerTrackingStore(state => state.retryAttempted);
  const retryPropertyInfoStatus = useHQVDatalayerTrackingStore(state => state.retryPropertyInfoStatus);
  const retryPhotogalleryStatus = useHQVDatalayerTrackingStore(state => state.retryPhotogalleryStatus);
  const hqvFallbackMode = useHQVDatalayerTrackingStore(state => state.hqvFallbackMode);

  // Store actions
  const setRetryAttempted = useHQVDatalayerTrackingStore(state => state.setRetryAttempted);
  const setShouldTriggerRetry = useHQVDatalayerTrackingStore(state => state.setShouldTriggerRetry);
  const setHqvFallbackMode = useHQVDatalayerTrackingStore(state => state.setHqvFallbackMode);
  const setDatalayerCallStatus = useHQVDatalayerTrackingStore(state => state.setDatalayerCallStatus);

  // Track if datalayer has been called for fallback scenarios
  const datalayerCalledForFallback = useRef(false);

  // Reset the ref when store is reset (detected by retryAttempted returning to false)
  useEffect(() => {
    if (!retryAttempted && datalayerCalledForFallback.current) {
      log.debug('Store reset detected - resetting datalayerCalledForFallback ref');
      datalayerCalledForFallback.current = false;
    }
  }, [retryAttempted]);

  // Helper to check if a call status indicates completion (success or error)
  const isCallComplete = useCallback((status: string) => status === 'success' || status === 'error', []);

  // Helper to check if a call failed
  const isCallFailed = useCallback((status: string) => status === 'error', []);

  // Helper to check if a call succeeded
  const isCallSucceeded = useCallback((status: string) => status === 'success', []);

  // Effect to trigger retry when both initial calls fail
  useEffect(() => {
    const bothInitialCallsComplete = isCallComplete(propertyInfoCallStatus) && isCallComplete(photogalleryCallStatus);
    const bothInitialCallsFailed = isCallFailed(propertyInfoCallStatus) && isCallFailed(photogalleryCallStatus);

    if (bothInitialCallsComplete && bothInitialCallsFailed && !retryAttempted) {
      log.debug('Both PropertyInfo and PhotoGallery calls failed. Triggering retry...');
      setRetryAttempted(true);
      setShouldTriggerRetry(true);
    }
  }, [
    propertyInfoCallStatus,
    photogalleryCallStatus,
    retryAttempted,
    isCallComplete,
    isCallFailed,
    setRetryAttempted,
    setShouldTriggerRetry,
  ]);

  // Effect to determine fallback mode after retry completes
  useEffect(() => {
    if (!retryAttempted) {
      return;
    }

    const retryComplete = isCallComplete(retryPropertyInfoStatus) && isCallComplete(retryPhotogalleryStatus);

    if (!retryComplete) {
      return;
    }

    const retrySucceeded = isCallSucceeded(retryPropertyInfoStatus) || isCallSucceeded(retryPhotogalleryStatus);

    if (retrySucceeded) {
      log.debug('Retry succeeded - setting fallback mode to normal');
      setHqvFallbackMode('normal');

      // Call data layer with fallback values when retry succeeds
      if (!datalayerCalledForFallback.current) {
        datalayerCalledForFallback.current = true;
        setDatalayerCallStatus('called');
        log.debug('Calling data layer with search_fallback_exists=true (retry succeeded)');
        callDataLayer(
          resolvedUrl,
          sessionData,
          headersData,
          query,
          propertyId,
          brandCode,
          datalayerParams,
          datalayerProperties,
          currentLocale,
          true, // isSearchFallback = true when retry succeeds
          'HQV_Failure'
        );
      }
      return;
    }

    // Both retries failed - check amenities status
    const amenitiesComplete = isCallComplete(amenitiesCallStatus);

    if (!amenitiesComplete) {
      log.debug('Waiting for amenities call to complete...');
      return;
    }

    if (isCallSucceeded(amenitiesCallStatus)) {
      log.debug('Retry failed but amenities succeeded - setting fallback mode to amenities_only');
      setHqvFallbackMode('amenities_only');

      // Call data layer with fallback values when amenities_only mode
      if (!datalayerCalledForFallback.current) {
        datalayerCalledForFallback.current = true;
        setDatalayerCallStatus('called');
        log.debug('Calling data layer with search_fallback_exists=true (amenities_only mode)');
        callDataLayer(
          resolvedUrl,
          sessionData,
          headersData,
          query,
          propertyId,
          brandCode,
          datalayerParams,
          datalayerProperties,
          currentLocale,
          true, // isSearchFallback = true for amenities_only
          'HQV_Failure'
        );
      }
    } else {
      log.debug('All calls failed - setting fallback mode to closed');
      setHqvFallbackMode('closed');

      // Call data layer with fallback values when closed mode
      if (!datalayerCalledForFallback.current) {
        datalayerCalledForFallback.current = true;
        setDatalayerCallStatus('called');
        log.debug('Calling data layer with search_fallback_exists=false (closed mode)');
        callDataLayer(
          resolvedUrl,
          sessionData,
          headersData,
          query,
          propertyId,
          brandCode,
          datalayerParams,
          datalayerProperties,
          currentLocale,
          false, // isSearchFallback = false for closed mode
          'HQV_Failure'
        );
      }
    }
  }, [
    retryAttempted,
    retryPropertyInfoStatus,
    retryPhotogalleryStatus,
    amenitiesCallStatus,
    isCallComplete,
    isCallSucceeded,
    setHqvFallbackMode,
    setDatalayerCallStatus,
    resolvedUrl,
    sessionData,
    headersData,
    query,
    propertyId,
    brandCode,
    datalayerParams,
    datalayerProperties,
    currentLocale,
  ]);

  // Effect to close modal when fallback mode is 'closed'
  useEffect(() => {
    if (hqvFallbackMode === 'closed' && onCloseModal) {
      log.debug('All HQV calls failed - closing modal');
      onCloseModal();
    }
  }, [hqvFallbackMode, onCloseModal]);

  // Determine if currently retrying
  const isRetrying = retryAttempted && (retryPropertyInfoStatus === 'loading' || retryPhotogalleryStatus === 'loading');

  return {
    hqvFallbackMode,
    isRetrying,
  };
};

export default useHQVRetryOrchestration;
